import { InnerBox } from "@/components/elements/InnerBox";
import { TalentListTable } from "@/features/talent/components/talent-list/TalentListTable";
import { TalentListTop } from "@/features/talent/components/talent-list/TalentListTop";
import { divider } from "@/theme/colors";
import { Box, Menu, MenuItem as MuiMenuItem, Stack, Typography } from "@mui/material";
import { FC, useState, useEffect, useMemo, useCallback, useRef, MouseEvent as ReactMouseEvent } from "react";
import { DEFAULT_PER_PAGE, START_PAGE } from "@/constants/pagination";
import { usePagination } from "@/hooks/usePagination";
import { deleteTalentApi, exportTalentApi, getTalentListApi, useTalentList } from "@/api/services/main/talent";
import { Pagination } from "@/components/elements/Pagination";
import { Button } from "@/components/elements/Button";
import { useDialog } from "@/hooks/useDialog";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { showError } from "@/utils/error";
import { downloadBlobResponse } from "@/utils/export";
import { TalentCannotDeleteModal } from "@/features/talent/components/talent-common/TalentCannotDeleteModal";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useSanitizedSortParams } from "@/hooks/useSanitizedSortParams";
import { useSanitizedPageParam } from "@/hooks/useSanitizedPageParam";
import { TALENT_SORTABLE_FIELDS } from "@/constants/sortable-fields";
import { TGetTalentListReqDto, TTalentListOutputDto } from "@/api/services/main/talent/dtos/get-talent-list.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { queryClient } from "@/constants/queryClient";
import { IconActionDelete, IconChevronDown, IconPlus } from "@/assets/icons";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { useMe } from "@/api/services/main/auth";
import dayjs from "dayjs";
import { useVisible } from "@/hooks/useVisible";
import { ImportTalentModal } from "@/features/talent/components/talent-import/ImportTalentModal";
import { ScanImageTalentModal } from "@/features/talent/components/talent-scan-image/ScanImageTalentModal";
import {
  TALENT_SELECTED_STORAGE_KEY,
  TALENT_FILTERS_STORAGE_KEY,
  TALENT_SELECT_ALL_STORAGE_KEY,
  TALENT_DISPLAY_COLUMNS_STORAGE_KEY,
  TALENT_ADVANCED_FILTERS_STORAGE_KEY,
  TALENT_APPLIED_CONDITION_NAME_STORAGE_KEY,
  TALENT_APPLIED_CONDITION_ID_STORAGE_KEY,
  TALENT_DEFAULT_SEEDED_STORAGE_KEY,
} from "@/features/talent/constants/common";
import { useSavedConditionList } from "@/api/services/main/saved-condition";
import { enforceLockedColumns, resolveSavedConditionColumns } from "@/components/commons/filter-condition/filter-condition-adapter-kit";
import { clearSessionFilterState, dropLegacyLocalFilterState, readSessionFilterState, writeSessionFilterState } from "@/utils/session-filter-state";
import { useListPageState } from "@/hooks/useListPageState";
import { AppRoutes } from "@/routes/config";
import { TTalentFilterFormExtended, TTalentDisplayColumnSetting } from "@/features/talent/components/talent-list/talent-filter-shared";
import {
  stripEmptyTalentFilterFields,
  normalizeTalentDateFields,
  normalizeTalentMultiSelectFields,
  isEmptyTalentFilterValue,
  defaultTalentFilterValues,
  defaultTalentDisplayColumns,
  mergeWithDefaultTalentColumns,
  TALENT_LOCKED_COLUMN_IDS,
} from "@/features/talent/components/talent-list/talent-filter-shared";

/** Convert string[] to number[], filtering out NaN values from corrupted data */
// Number("") === 0 (not NaN), so filter empties first to avoid silent 0 values in numeric id filters
const toNumberArray = (values: string[]): number[] =>
  values
    .filter((v) => v !== "" && v !== null && v !== undefined)
    .map(Number)
    .filter((n) => Number.isFinite(n));

type TTalentListPageProps = {};

type TTalentFilterForm = {
  keyword: string;
};

const defaultValues: TTalentFilterForm = {
  keyword: "",
};

const shouldPreserveSelection = (locationState: unknown): boolean => {
  if (!locationState || typeof locationState !== "object") return false;
  const state = locationState as { preserveSelection?: boolean };
  return state.preserveSelection === true;
};

export const TalentListPage: FC<TTalentListPageProps> = () => {
  const pagReq = usePagination();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const scanImageTalentModal = useVisible();
  const importPersonnelModal = useVisible();
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const isMoreMenuOpen = Boolean(moreAnchorEl);

  useSanitizedPageParam({ basePath: AppRoutes.talentList });
  const { sortValue } = useSanitizedSortParams({
    allowedFields: [...TALENT_SORTABLE_FIELDS],
  });

  const selectionInitializedRef = useRef(false);
  if (!selectionInitializedRef.current) {
    selectionInitializedRef.current = true;
    if (!shouldPreserveSelection(location.state)) {
      sessionStorage.removeItem(TALENT_SELECTED_STORAGE_KEY);
      sessionStorage.removeItem(TALENT_SELECT_ALL_STORAGE_KEY);
    }
  }

  const { filters } = useListPageState<TTalentFilterForm>({
    storageKey: TALENT_FILTERS_STORAGE_KEY,
    defaultFilters: defaultValues,
    basePath: "/talent",
    legacyCheckField: "keyword",
  });
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const [selectionUpdateTrigger, setSelectionUpdateTrigger] = useState<number>(0);
  const [selectionResetKey, setSelectionResetKey] = useState<number>(0);
  const [isExport, setIsExport] = useState<boolean>(false);
  const [appliedConditionName, setAppliedConditionName] = useState<string>(
    () => readSessionFilterState<string>(TALENT_APPLIED_CONDITION_NAME_STORAGE_KEY) ?? "",
  );
  const [appliedConditionId, setAppliedConditionId] = useState<number | null>(() =>
    readSessionFilterState<number>(TALENT_APPLIED_CONDITION_ID_STORAGE_KEY),
  );

  const handleAppliedConditionNameChange = useCallback((name: string) => {
    setAppliedConditionName(name);
    if (name) {
      writeSessionFilterState(TALENT_APPLIED_CONDITION_NAME_STORAGE_KEY, name);
    } else {
      clearSessionFilterState(TALENT_APPLIED_CONDITION_NAME_STORAGE_KEY);
    }
  }, []);

  const handleAppliedConditionIdChange = useCallback((id: number | null) => {
    setAppliedConditionId(id);
    if (id !== null) {
      writeSessionFilterState(TALENT_APPLIED_CONDITION_ID_STORAGE_KEY, id);
    } else {
      clearSessionFilterState(TALENT_APPLIED_CONDITION_ID_STORAGE_KEY);
    }
  }, []);

  const [advancedFilters, setAdvancedFilters] = useState<TTalentFilterFormExtended | null>(() => {
    const saved = readSessionFilterState<Partial<TTalentFilterFormExtended>>(TALENT_ADVANCED_FILTERS_STORAGE_KEY);
    if (!saved) return null;
    return {
      ...defaultTalentFilterValues,
      ...normalizeTalentMultiSelectFields(normalizeTalentDateFields(saved) as Partial<Record<string, unknown>>),
    } as TTalentFilterFormExtended;
  });

  useEffect(() => {
    dropLegacyLocalFilterState([
      TALENT_ADVANCED_FILTERS_STORAGE_KEY,
      TALENT_APPLIED_CONDITION_NAME_STORAGE_KEY,
      TALENT_APPLIED_CONDITION_ID_STORAGE_KEY,
    ]);
  }, []);

  const { data: savedConditionsData } = useSavedConditionList("talent");
  const hasSeededDefault = useRef(readSessionFilterState<boolean>(TALENT_DEFAULT_SEEDED_STORAGE_KEY) ?? false);

  useEffect(() => {
    if (hasSeededDefault.current || !savedConditionsData) return;
    hasSeededDefault.current = true;
    writeSessionFilterState(TALENT_DEFAULT_SEEDED_STORAGE_KEY, true);

    const fallback = savedConditionsData.data.find((item) => item.isDefault);
    if (!fallback) return;

    const filters = {
      ...defaultTalentFilterValues,
      ...normalizeTalentMultiSelectFields(normalizeTalentDateFields(fallback.filters ?? {}) as Partial<Record<string, unknown>>),
    } as TTalentFilterFormExtended;
    setAdvancedFilters(filters);
    writeSessionFilterState(TALENT_ADVANCED_FILTERS_STORAGE_KEY, stripEmptyTalentFilterFields(filters));
    setAppliedConditionName(fallback.title);
    writeSessionFilterState(TALENT_APPLIED_CONDITION_NAME_STORAGE_KEY, fallback.title);
    setAppliedConditionId(Number(fallback.id));
    writeSessionFilterState(TALENT_APPLIED_CONDITION_ID_STORAGE_KEY, Number(fallback.id));
    const seededColumns = resolveSavedConditionColumns(fallback, defaultTalentDisplayColumns);
    setDisplayColumnSettings(seededColumns);
    localStorage.setItem(TALENT_DISPLAY_COLUMNS_STORAGE_KEY, JSON.stringify(seededColumns));
  }, [savedConditionsData]);

  const resetToFirstPage = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(START_PAGE));
      return next;
    });
  }, [setSearchParams]);

  const handleApplyAdvancedFilters = useCallback(
    (filters: TTalentFilterFormExtended) => {
      const hasAnyFilter = Object.values(filters).some((v) => !isEmptyTalentFilterValue(v));
      if (hasAnyFilter) {
        setAdvancedFilters(filters);
        writeSessionFilterState(TALENT_ADVANCED_FILTERS_STORAGE_KEY, stripEmptyTalentFilterFields(filters));
      } else {
        setAdvancedFilters(null);
        clearSessionFilterState(TALENT_ADVANCED_FILTERS_STORAGE_KEY);
      }
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  const [displayColumnSettings, setDisplayColumnSettings] = useState<TTalentDisplayColumnSetting[] | null>(() => {
    const saved = localStorage.getItem(TALENT_DISPLAY_COLUMNS_STORAGE_KEY);
    if (saved) {
      try {
        // Merge with defaults so newly-added columns appear for users with stale saved settings
        return mergeWithDefaultTalentColumns(JSON.parse(saved));
      } catch {
        return null;
      }
    }
    return null;
  });
  const lockedDisplayColumnSettings = useMemo(
    () => displayColumnSettings && enforceLockedColumns(displayColumnSettings, TALENT_LOCKED_COLUMN_IDS),
    [displayColumnSettings],
  );

  const handleApplyDisplaySettings = useCallback((columns: TTalentDisplayColumnSetting[]) => {
    setDisplayColumnSettings(columns);
  }, []);

  // [TEST-ONLY] full-text search input (BE: searchFullText via sidecar + pg_bigm). Remove when done testing.
  const [searchFullText, setSearchFullText] = useState("");
  const handleSearchFullText = useCallback(
    (value: string) => {
      setSearchFullText(value.trim());
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  const [isSelectAll, setIsSelectAll] = useState<boolean>(() => {
    return sessionStorage.getItem(TALENT_SELECT_ALL_STORAGE_KEY) === "true";
  });
  const [excludedIds, setExcludedIds] = useState<number[]>(() => {
    if (sessionStorage.getItem(TALENT_SELECT_ALL_STORAGE_KEY) === "true") {
      const raw = sessionStorage.getItem(TALENT_SELECTED_STORAGE_KEY);
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
        return [];
      } catch {
        return [];
      }
    }
    return [];
  });

  const { data: meResult } = useMe();

  // Build query with both basic and advanced filter
  const query: TGetTalentListReqDto = {
    page: pagReq.page || START_PAGE,
    perPage: pagReq.perPage || DEFAULT_PER_PAGE,
    ...(searchFullText ? { searchFullText } : {}), // [TEST-ONLY]
    ...(filters.keyword ? { keyword: filters.keyword } : {}),
    ...(advancedFilters && {
      ...(advancedFilters.talentNumber ? { talentNumber: advancedFilters.talentNumber } : {}),
      ...(advancedFilters.fullName ? { fullName: advancedFilters.fullName } : {}),
      ...(advancedFilters.nickName ? { nickName: advancedFilters.nickName } : {}),
      ...(advancedFilters.nationalityId?.length ? { nationalityId: toNumberArray(advancedFilters.nationalityId) } : {}),
      ...(advancedFilters.gender?.length ? { gender: toNumberArray(advancedFilters.gender) } : {}),
      ...(advancedFilters.recruitmentStatus?.length ? { recruitmentStatus: toNumberArray(advancedFilters.recruitmentStatus) } : {}),
      ...(advancedFilters.residenceStatusId?.length ? { residenceStatusId: toNumberArray(advancedFilters.residenceStatusId) } : {}),
      ...(advancedFilters.languageTestLevelId?.length ? { languageTestLevelId: toNumberArray(advancedFilters.languageTestLevelId) } : {}),
      ...(advancedFilters.companySpecificIndustryId?.length
        ? { companySpecificIndustryId: toNumberArray(advancedFilters.companySpecificIndustryId) }
        : {}),
      ...(advancedFilters.personInChargeId?.length ? { personInChargeId: toNumberArray(advancedFilters.personInChargeId) } : {}),
      ...(advancedFilters.registrationDateFrom ? { registrationDateFrom: dayjs(advancedFilters.registrationDateFrom).format("YYYY-MM-DD") } : {}),
      ...(advancedFilters.registrationDateTo ? { registrationDateTo: dayjs(advancedFilters.registrationDateTo).format("YYYY-MM-DD") } : {}),
      ...(advancedFilters.desiredWorkLocation ? { desiredWorkLocation: advancedFilters.desiredWorkLocation } : {}),
      ...(advancedFilters.location?.length ? { location: toNumberArray(advancedFilters.location) } : {}),
      ...(advancedFilters.latestProgress?.length ? { latestProgress: toNumberArray(advancedFilters.latestProgress) } : {}),
      ...(advancedFilters.heldQualifications ? { heldQualifications: advancedFilters.heldQualifications } : {}),
      ...(advancedFilters.educationLevelId?.length ? { educationLevelId: toNumberArray(advancedFilters.educationLevelId) } : {}),
      ...(advancedFilters.isRecommendedTalent === "1" ? { isRecommendedTalent: 1 } : {}),
      ...(advancedFilters.partnerId?.length ? { partnerId: toNumberArray(advancedFilters.partnerId) } : {}),
    }),
  };

  if (sortValue) {
    query.sorts = [sortValue as `${string}|${string}`];
  }

  const { data, isLoading } = useTalentList(query);

  // Cache talent data across pages for cross-page case match validation during deletion
  const talentDataCacheRef = useRef<Map<number, TTalentListOutputDto>>(new Map());
  useEffect(() => {
    if (data?.data) {
      data.data.forEach((talent) => talentDataCacheRef.current.set(talent.id, talent));
    }
  }, [data?.data]);

  const confirmDelete = useDialog();
  const errorDelete = useDialog();
  const [talentNamesCannotDelete, setTalentNamesCannotDelete] = useState<string[]>([]);
  const [deleteCount, setDeleteCount] = useState<number>(0);

  const getTalentSelected = (): number[] => {
    const raw = sessionStorage.getItem(TALENT_SELECTED_STORAGE_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      return Object.values(parsed).flat() as number[];
    } catch {
      return [];
    }
  };

  const clearTalentSelected = () => {
    sessionStorage.removeItem(TALENT_SELECTED_STORAGE_KEY);
    sessionStorage.removeItem(TALENT_SELECT_ALL_STORAGE_KEY);
  };

  const isSelectionActive = useMemo(() => {
    if (isSelectAll) return true;
    return getTalentSelected().length > 0;
  }, [isSelectAll, selectionUpdateTrigger]);

  const selectedCount = useMemo(() => {
    if (isSelectAll) {
      const total = data?.pagination?.total ?? 0;
      return Math.max(0, total - excludedIds.length);
    }
    return getTalentSelected().length;
  }, [isSelectAll, excludedIds, data?.pagination?.total, selectionUpdateTrigger]);

  const handleSelectionChange = useCallback((selectedIds: number[], isAllSelected: boolean) => {
    setIsSelectAll(isAllSelected);
    if (isAllSelected) {
      setExcludedIds(selectedIds);
    } else {
      setExcludedIds([]);
    }
    setSelectionUpdateTrigger((prev) => prev + 1);
  }, []);

  const handleClickExport = async (isExport: boolean) => {
    if (!isExport) return;
    const talentIds = isSelectAll ? excludedIds : getTalentSelected();
    await handleExport(talentIds);
  };

  const handleExport = async (talentIds: number[]) => {
    if (!isSelectAll && talentIds.length === 0) return;
    try {
      setIsExport(true);
      const res = await exportTalentApi(talentIds, isSelectAll, {
        keyword: query.keyword,
        nationalityId: query.nationalityId,
        companySpecificIndustryId: query.companySpecificIndustryId,
        recruitmentStatus: query.recruitmentStatus,
      });
      const matchName = meResult?.data?.match?.matchName ?? "";
      downloadBlobResponse(res, `${matchName}_人材DB_${dayjs().unix()}.xlsx`);
    } catch (e) {
      showError(e);
    }
    setIsExport(false);
  };

  const handleDelete = async () => {
    let deleteIds: number[] = [];
    let talentsToValidate: TTalentListOutputDto[] = [];

    if (isSelectAll) {
      // Fetch ALL talents matching current filter to get explicit IDs (never send isAll=true to delete API)
      const totalCount = data?.pagination?.total ?? 0;
      if (totalCount === 0) return;

      try {
        setIsSubmit(true);
        const allTalentsRes = await getTalentListApi({ ...query, page: START_PAGE, perPage: totalCount });
        const allTalents = allTalentsRes.data ?? [];

        talentsToValidate = allTalents.filter((talent) => !excludedIds.includes(talent.id));
        deleteIds = talentsToValidate.map((t) => t.id);
      } catch (error) {
        showError(error);
        setIsSubmit(false);
        return;
      }
    } else {
      deleteIds = getTalentSelected();
      if (deleteIds.length === 0) return;

      talentsToValidate = deleteIds
        .map((id) => talentDataCacheRef.current.get(id))
        .filter((talent): talent is TTalentListOutputDto => talent !== undefined);
    }

    const talentsWithCaseMatches = talentsToValidate.filter((talent) => talent.caseMatchStatus != null && talent.caseMatchStatus >= 0);

    if (talentsWithCaseMatches.length > 0) {
      const talentNames = talentsWithCaseMatches.map((talent) => talent.fullName || "").filter(Boolean);
      setTalentNamesCannotDelete(talentNames);
      setIsSubmit(false);
      await errorDelete.show();
      return;
    }

    setDeleteCount(deleteIds.length);
    if (!isSelectAll) setIsSubmit(true);
    try {
      const agreeToDelete = await confirmDelete.show();
      if (!agreeToDelete) {
        setIsSubmit(false);
        return;
      }

      const rs = await deleteTalentApi(deleteIds, false);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      clearTalentSelected();
      setIsSelectAll(false);
      setExcludedIds([]);
      talentDataCacheRef.current.clear();
      setSelectionResetKey((prev) => prev + 1);
      setSelectionUpdateTrigger((prev) => prev + 1);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TALENT.FETCH_LIST] });
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmit(false);
    }
  };

  // handle case reload page
  useEffect(() => {
    const navEntries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    const isReload = navEntries.length > 0 && navEntries[0].type === "reload";

    if (isReload) {
      sessionStorage.removeItem(TALENT_SELECTED_STORAGE_KEY);
      sessionStorage.removeItem(TALENT_SELECT_ALL_STORAGE_KEY);
    }
  }, []);

  return (
    <>
      <Stack minHeight="100%">
        <Stack mb={3} justifyContent="space-between" alignItems="center" direction="row" width="100%">
          <Typography variant="head20Bold">人材一覧</Typography>
          <Box display="flex" gap={2}>
            <Box>
              <Button
                variant="outline"
                size="sm"
                endIcon={
                  <IconChevronDown
                    style={{
                      transition: "transform 0.2s",
                      transform: isMoreMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                }
                onClick={(e: ReactMouseEvent<HTMLButtonElement>) => setMoreAnchorEl(e.currentTarget)}
              >
                <Typography variant="body14Semi">AI取り込み / インポート</Typography>
              </Button>
              <Menu
                anchorEl={moreAnchorEl}
                open={isMoreMenuOpen}
                onClose={() => setMoreAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 0.5,
                      borderRadius: "8px",
                      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
                      minWidth: "220px",
                    },
                  },
                }}
                sx={{
                  ".MuiPaper-root": {
                    padding: 0,
                  },
                  ".MuiMenu-list": {
                    padding: 0,
                  },
                }}
              >
                <MuiMenuItem
                  onClick={() => {
                    setMoreAnchorEl(null);
                    scanImageTalentModal.open();
                  }}
                  sx={{ py: 1.5 }}
                >
                  <Typography variant="body14">履歴書などファイルからAI取込</Typography>
                </MuiMenuItem>
                <Box sx={{ width: "100%", borderTop: `1px solid ${divider.high}` }}></Box>
                <MuiMenuItem
                  onClick={() => {
                    setMoreAnchorEl(null);
                    handleClickExport(true);
                  }}
                  disabled={!isSelectionActive || isExport}
                  sx={{ py: 1.5 }}
                >
                  <Typography variant="body14">Excelエクスポート</Typography>
                </MuiMenuItem>
                <MuiMenuItem
                  onClick={() => {
                    setMoreAnchorEl(null);
                    importPersonnelModal.open();
                  }}
                  sx={{ py: 1.5 }}
                >
                  <Typography variant="body14">一括インポート</Typography>
                </MuiMenuItem>
              </Menu>
            </Box>
            <Button component={Link} to={AppRoutes.talentCreate} size="sm" sx={{ width: "144px" }} startIcon={<IconPlus width={20} height={20} />}>
              <Typography variant="body14Semi">新規人材登録</Typography>
            </Button>
          </Box>
        </Stack>
        <InnerBox padding={3} showDivider={false} bordered={true} borderColor={divider.high}>
          <Stack>
            {/* Search and Filter Section */}
            <TalentListTop
              pagination={data?.pagination}
              // [TEST-ONLY] full-text search (IROHANA_MATCH_DEV-383). Default OFF; shown only when
              // REACT_APP_TALENT_FULLTEXT_SEARCH_ENABLED is exactly "true" (e.g. staging).
              onSearchFullText={
                process.env.REACT_APP_TALENT_FULLTEXT_SEARCH_ENABLED === "true" ? handleSearchFullText : undefined
              }
              onApplyAdvancedFilters={handleApplyAdvancedFilters}
              onApplyDisplaySettings={handleApplyDisplaySettings}
              appliedConditionName={appliedConditionName}
              appliedConditionId={appliedConditionId}
              onAppliedConditionNameChange={handleAppliedConditionNameChange}
              onAppliedConditionIdChange={handleAppliedConditionIdChange}
              currentDisplayColumns={lockedDisplayColumnSettings}
              currentAdvancedFilters={advancedFilters}
              selectionProps={{
                selectedCount,
                query,
                getTalentSelected,
                isSelectAll,
                excludedIds,
              }}
            />

            {/* Data Table */}
            <TalentListTable
              rows={data?.data ?? []}
              isLoading={isLoading}
              onSelectionChange={handleSelectionChange}
              displayColumnSettings={lockedDisplayColumnSettings}
              selectionResetKey={selectionResetKey}
            />

            <Stack mt={3} direction="row" justifyContent="space-between" alignItems="center">
              <Pagination totalPage={data?.pagination?.totalPage ?? 1} />
              <Button
                variant="danger"
                size="sm"
                color="error"
                disabled={isSubmit || !isSelectionActive || isSelectAll}
                onClick={handleDelete}
                sx={{ width: "88px", mr: 4 }}
                startIcon={<IconActionDelete />}
              >
                <Typography variant="body14Semi">削除</Typography>
              </Button>
            </Stack>
          </Stack>
        </InnerBox>
      </Stack>

      <ConfirmDeleteModal
        open={confirmDelete.open}
        onOk={confirmDelete.confirm}
        onClose={confirmDelete.cancel}
        message={
          <>
            {deleteCount}名の人材情報を削除しますがよろしいですか？
            <br />
            この操作は元に戻せないため、ご注意ください。
          </>
        }
        title=""
      />

      <TalentCannotDeleteModal open={errorDelete.open} onClose={errorDelete.cancel} talentNames={talentNamesCannotDelete} />
      <ImportTalentModal modal={importPersonnelModal} />
      <ScanImageTalentModal modal={scanImageTalentModal} />
    </>
  );
};
